import { Color, type Object3D, Vector3 } from 'three'
import type { ToonMaterial } from './toonMaterial'

/**
 * `ConveyorIndicator` — the capacity sign beside the road, and its alarm.
 *
 * 🔍 `ConveyorIndicator$$UpdateIndicator__0x211825c.c`, `$$StartAlarm__0x2118594.c`,
 * `$$StopAlarm__0x2117f5c.c`, `$$PulseIndicator__0x2118408.c`,
 * `$$Awake__0x2117ba4.c`; fields at `dump.cs:711028`.
 *
 *     UpdateIndicator(current, max, isSpaceMaxed):
 *         if (limitText != null)
 *             limitText.text = (current >= max || !isSpaceMaxed)
 *                 ? string.Format(<fmt>, current, max)
 *                 : <a second literal>;
 *         if (_lastCurrentBuses != -1 && _lastCurrentBuses < current) PulseIndicator();
 *         _lastCurrentBuses = current;
 *         if (current < max && !isSpaceMaxed) StopAlarm(); else StartAlarm();
 *
 * The number is drawn by `gameUi.ts` (the label is a world-space `TextMeshPro`,
 * which the port projects into the overlay); this class owns everything else.
 *
 * ## What the alarm actually looks like
 *
 * `StartAlarm` does three things, and the third is the one that carries across
 * the whole screen:
 *
 *  - `glowParent.gameObject.SetActive(true)` — the two red lamps on the sign,
 *    which sit `m_IsActive: 0` in the scene and are switched on here;
 *  - every glow renderer scales to **1.5x** and the sign itself to **1.03x**,
 *    joined in one 0.5 s sequence looped `-1` with `LoopType.Yoyo`;
 *  - `_splineMaterial.DOColor(VertexColorGColor, (1, 0.43, 0.43, 1), 0.5f)` —
 *    the **road's kerb turns pink-red**. `RoadMain`'s G palette entry is
 *    (0.992, 1, 1), the near-white edging, so this is the whole loop lighting up.
 *
 * `reference/gameplay.mp4` at t = 9.25 s shows exactly that: sign reading 5/5,
 * both lamps red, and the kerb red all the way round.
 *
 * ❓ Every `SetEase` argument is gone with the rest of them (IL2CPP drops the
 * enum), so the interpolation here is linear. The durations, the scales and the
 * colour are all 🔍.
 */

/** `0x3f000000` — the alarm sequence's duration; yoyo makes the cycle twice it. */
const ALARM_SECONDS = 0.5
/** `0x3e19999a` — each half of the pulse. */
const PULSE_SECONDS = 0.15
const GLOW_SCALE = 1.5
const SIGN_ALARM_SCALE = 1.03
const PULSE_SCALE = 1.2
/**
 * `DOColor(1, 0.43, 0.43, 1)` on `_VertexColorGColor`.
 *
 * Built through `setRGB(..., 'srgb')` for the reason `CLAUDE.md` §8 gives about
 * `.mat` files: a Unity `Color` literal is sRGB, and the palette uniforms this
 * lerps against were converted the same way in `toonColor`. Handing `new Color`
 * the raw triple would put a linear value next to sRGB ones and read washed out.
 */
const ALARM_KERB_COLOR = new Color().setRGB(1, 0.43, 0.43, 'srgb')

const _scale = new Vector3()

export class ConveyorIndicatorView {
  private readonly baseScale = new Vector3()
  private readonly glowParent: Object3D | null
  /** `_initialScales` — the dictionary `Awake` fills from `glowRenderers`. */
  private readonly glowScales: { object: Object3D; scale: Vector3 }[] = []

  private readonly kerbOriginal = new Color()
  private readonly kerbCurrent = new Color()

  /** `_isAlarmActive` (0x68). */
  private alarmActive = false
  /** `_lastCurrentBuses` (0x6C), which `ResetIndicator` seeds at -1. */
  private lastCurrent = -1

  private alarmPhase = 0
  /** Null unless a pulse is running; seconds elapsed into it. */
  private pulseTime: number | null = null

  constructor(
    private readonly sign: Object3D,
    /** `splineMesh`'s material — `RoadMain`, whose G entry is the kerb. */
    private readonly roadMaterial: ToonMaterial | null,
  ) {
    this.baseScale.copy(sign.scale)

    this.glowParent = sign.getObjectByName('GlowParent') ?? null
    if (this.glowParent) {
      this.glowParent.visible = false
      for (const child of this.glowParent.children) {
        this.glowScales.push({ object: child, scale: child.scale.clone() })
      }
    }

    const g = this.roadMaterial?.uniforms.uPaletteG?.value as Color | undefined
    if (g) {
      this.kerbOriginal.copy(g)
      this.kerbCurrent.copy(g)
    }
  }

  /**
   * `UpdateIndicator` plus the frame the tweens would have been driven on.
   *
   * The original runs this from `SplineController.RefreshConveyorIndicator`,
   * which the port mirrors in `session.conveyorIndicator`.
   */
  update(current: number, max: number, spaceMaxed: boolean, dt: number): void {
    if (this.lastCurrent !== -1 && this.lastCurrent < current) this.pulse()
    this.lastCurrent = current

    if (current < max && !spaceMaxed) this.stopAlarm()
    else this.startAlarm()

    this.advance(dt)
  }

  /** `ResetIndicator` — called on `SplineController.LevelLoaded`. */
  reset(): void {
    this.lastCurrent = -1
    this.stopAlarm()
    this.pulseTime = null
    this.sign.scale.copy(this.baseScale)
  }

  /** `StartAlarm` — early-outs when already alarmed, so the phase survives. */
  private startAlarm(): void {
    if (this.alarmActive) return
    this.alarmActive = true
    // `_pulseSequence.Kill()` — a pulse in flight is dropped, not finished.
    this.pulseTime = null
    if (this.glowParent) this.glowParent.visible = true
    this.alarmPhase = 0
  }

  /** `StopAlarm` — restores the base scale, the glow and the kerb colour. */
  private stopAlarm(): void {
    if (!this.alarmActive) return
    this.alarmActive = false
    this.sign.scale.copy(this.baseScale)
    if (this.glowParent) this.glowParent.visible = false
    for (const { object, scale } of this.glowScales) object.scale.copy(scale)
    this.setKerb(this.kerbOriginal)
  }

  /**
   * `PulseIndicator` — one 1.2x bump when the count goes **up**.
   *
   * The `if (_isAlarmActive) return` at the top is why a sign that is already
   * alarmed does not also bounce.
   */
  private pulse(): void {
    if (this.alarmActive) return
    this.pulseTime = 0
    this.sign.scale.copy(this.baseScale)
  }

  private advance(dt: number): void {
    if (this.alarmActive) {
      // One `SetLoops(-1, LoopType.Yoyo)` over a 0.5 s sequence: 0 -> 1 -> 0.
      this.alarmPhase = (this.alarmPhase + dt) % (ALARM_SECONDS * 2)
      const half = this.alarmPhase / ALARM_SECONDS
      const k = half <= 1 ? half : 2 - half

      _scale.copy(this.baseScale).multiplyScalar(1 + (SIGN_ALARM_SCALE - 1) * k)
      this.sign.scale.copy(_scale)
      for (const { object, scale } of this.glowScales) {
        object.scale.copy(scale).multiplyScalar(1 + (GLOW_SCALE - 1) * k)
      }
      this.setKerb(this.kerbCurrent.copy(this.kerbOriginal).lerp(ALARM_KERB_COLOR, k))
      return
    }

    if (this.pulseTime === null) return
    this.pulseTime += dt
    const total = PULSE_SECONDS * 2
    if (this.pulseTime >= total) {
      this.pulseTime = null
      this.sign.scale.copy(this.baseScale)
      return
    }
    // `Append`, not `Join`: out then back, so the ramp is a triangle.
    const half = this.pulseTime / PULSE_SECONDS
    const k = half <= 1 ? half : 2 - half
    this.sign.scale.copy(this.baseScale).multiplyScalar(1 + (PULSE_SCALE - 1) * k)
  }

  private setKerb(color: Color): void {
    const g = this.roadMaterial?.uniforms.uPaletteG?.value as Color | undefined
    if (g) g.copy(color)
  }

  /** Test seam. */
  get isAlarmActive(): boolean {
    return this.alarmActive
  }

  get glowVisible(): boolean {
    return this.glowParent?.visible ?? false
  }
}
